import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, Heart, Star, ShoppingBag, ChevronRight, Sparkles, Zap, TrendingUp, Quote } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 20: BAZAAR — Marketplace Vibrant
 * Style: Vibrant, bustling, colorful marketplace energy with magenta accents
 * Best for: Indian fashion marketplace, multi-category e-commerce
 * Hero: Animated "THE BAZAAR" marquee text, product images, magenta CTA
 */
const Template20 = () => {
    const navigate = useNavigate();
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const [email, setEmail] = useState('');
    const [hoveredProduct, setHoveredProduct] = useState(null);
    const [hoveredCategory, setHoveredCategory] = useState(null);
    const touchStartX = useRef(0);

    // Categories
    const categories = [
        { name: 'Sarees', image: 'https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=400', count: '240+ Styles', color: '#C026D3' },
        { name: 'Lehengas', image: 'https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=400', count: '180+ Styles', color: '#A21CAF' },
        { name: 'Kurtas', image: 'https://images.pexels.com/photos/2220316/pexels-photo-2220316.jpeg?auto=compress&cs=tinysrgb&w=400', count: '320+ Styles', color: '#D946EF' },
        { name: 'Jewelry', image: 'https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=400', count: '150+ Pieces', color: '#E879F9' },
        { name: 'Bags', image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=400', count: '90+ Styles', color: '#F0ABFC' },
        { name: 'Footwear', image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400', count: '110+ Styles', color: '#C026D3' },
    ];

    // Products
    const products = [
        { id: 1, name: 'Banarasi Silk Saree', price: 4999, originalPrice: 6999, badge: 'HOT', category: 'Sarees', image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 2, name: 'Embroidered Anarkali Kurta', price: 2499, category: 'Kurtas', image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 3, name: 'Kundan Bridal Necklace', price: 12999, originalPrice: 15999, badge: 'TRENDING', category: 'Jewelry', image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 4, name: 'Cotton Palazzo Set', price: 1299, category: 'Kurtas', image: 'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 5, name: 'Zari Work Lehenga', price: 8999, originalPrice: 11999, badge: 'HOT', category: 'Lehengas', image: 'https://images.pexels.com/photos/6712360/pexels-photo-6712360.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 6, name: 'Temple Gold Jhumkas', price: 1499, category: 'Jewelry', image: 'https://images.pexels.com/photos/7275385/pexels-photo-7275385.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 7, name: 'Designer Potli Bag', price: 899, badge: 'NEW', category: 'Bags', image: 'https://images.pexels.com/photos/8953474/pexels-photo-8953474.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 8, name: 'Block Print Kurta Set', price: 1799, originalPrice: 2299, category: 'Kurtas', image: 'https://images.pexels.com/photos/2955375/pexels-photo-2955375.jpeg?auto=compress&cs=tinysrgb&w=400' },
    ];

    // Trending items
    const trending = [
        { name: 'Chanderi Sarees', tag: 'Up 45%', image: 'https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { name: 'Oxidized Jewelry', tag: 'Up 60%', image: 'https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { name: 'Indigo Kurtas', tag: 'Up 35%', image: 'https://images.pexels.com/photos/2220316/pexels-photo-2220316.jpeg?auto=compress&cs=tinysrgb&w=400' },
    ];

    // Testimonials
    const testimonials = [
        {
            text: 'The saree quality is absolutely stunning! The colors are so vibrant and the fabric drapes beautifully. Best marketplace for Indian fashion.',
            name: 'Priya Sharma',
            city: 'Mumbai',
            rating: 5,
        },
        {
            text: 'Found the most gorgeous lehenga for my sister\'s wedding at an unbeatable price. The Bazaar never disappoints!',
            name: 'Anita Verma',
            city: 'Delhi',
            rating: 5,
        },
        {
            text: 'Amazing collection of oxidized jewelry. Each piece is unique and the craftsmanship is top-notch. Will definitely shop again!',
            name: 'Kavitha Nair',
            city: 'Kochi',
            rating: 5,
        },
    ];

    // Deal strip items
    const deals = [
        'FLAT 40% OFF on Sarees',
        'Buy 2 Get 1 Free on Kurtas',
        'Free Shipping above \u20B9999',
        'New Arrivals Every Week',
        'Handloom Festival Sale LIVE',
        'Extra 10% off with UPI',
    ];

    // Auto-rotate testimonials
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    // Touch swipe for testimonials
    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            setActiveTestimonial((prev) => diff > 0 ? (prev + 1) % testimonials.length : (prev - 1 + testimonials.length) % testimonials.length);
        }
    };

    const toggleWishlist = (e, id) => { e.stopPropagation(); setWishlist((prev) => ({ ...prev, [id]: !prev[id] })); };

    return (
        <div style={{ fontFamily: "'Nunito', -apple-system, BlinkMacSystemFont, sans-serif" }} className="w-full min-h-screen bg-white">
            {/* Google Fonts */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Rubik:wght@400;500;600;700&family=Nunito:wght@300;400;600&display=swap');

                @keyframes bazaarMarquee {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(-50%); }
                }
                @keyframes bazaarFloat {
                    0%, 100% { transform: translateY(0) rotate(0deg); }
                    33% { transform: translateY(-15px) rotate(2deg); }
                    66% { transform: translateY(8px) rotate(-1deg); }
                }
                @keyframes dealScroll {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(-50%); }
                }
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(30px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .bazaar-hero-text {
                    animation: bazaarMarquee 20s linear infinite;
                }
                .bazaar-float-1 { animation: bazaarFloat 6s ease-in-out infinite; }
                .bazaar-float-2 { animation: bazaarFloat 7s ease-in-out infinite 1s; }
                .bazaar-float-3 { animation: bazaarFloat 5s ease-in-out infinite 2s; }
                .bazaar-float-4 { animation: bazaarFloat 8s ease-in-out infinite 0.5s; }
                .deal-scroll {
                    animation: dealScroll 25s linear infinite;
                }
                .bazaar-product-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 12px;
                }
                @media (min-width: 640px) {
                    .bazaar-product-grid { gap: 16px; }
                }
                @media (min-width: 768px) {
                    .bazaar-product-grid {
                        grid-template-columns: repeat(3, 1fr);
                        gap: 20px;
                    }
                }
                @media (min-width: 1024px) {
                    .bazaar-product-grid {
                        grid-template-columns: repeat(4, 1fr);
                        gap: 24px;
                    }
                }
            `}</style>

            {/* ===== SECTION 1: ANIMATED HERO ===== */}
            <section className="relative w-full min-h-[90vh] sm:min-h-screen overflow-hidden bg-white flex flex-col items-center justify-center">
                {/* Floating Product Images */}
                <div className="absolute inset-0 pointer-events-none hidden md:block">
                    <img
                        src="https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=400"
                        alt=""
                        className="bazaar-float-1 absolute top-[10%] left-[5%] w-32 lg:w-44 rounded-2xl shadow-2xl object-cover aspect-[3/4] opacity-80"
                    />
                    <img
                        src="https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=400"
                        alt=""
                        className="bazaar-float-2 absolute top-[15%] right-[8%] w-28 lg:w-40 rounded-2xl shadow-2xl object-cover aspect-[3/4] opacity-80"
                    />
                    <img
                        src="https://images.pexels.com/photos/2220316/pexels-photo-2220316.jpeg?auto=compress&cs=tinysrgb&w=400"
                        alt=""
                        className="bazaar-float-3 absolute bottom-[20%] left-[12%] w-24 lg:w-36 rounded-2xl shadow-2xl object-cover aspect-[3/4] opacity-70"
                    />
                    <img
                        src="https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=400"
                        alt=""
                        className="bazaar-float-4 absolute bottom-[15%] right-[5%] w-28 lg:w-36 rounded-2xl shadow-2xl object-cover aspect-[3/4] opacity-70"
                    />
                </div>

                {/* Subtle Background Pattern */}
                <div className="absolute inset-0 opacity-[0.03]" style={{
                    backgroundImage: 'radial-gradient(#C026D3 1px, transparent 1px)',
                    backgroundSize: '24px 24px',
                }} />

                {/* Animated Marquee Text */}
                <div className="w-full overflow-hidden mb-4 sm:mb-6">
                    <div className="bazaar-hero-text flex whitespace-nowrap">
                        {[...Array(4)].map((_, i) => (
                            <span
                                key={i}
                                className="text-6xl sm:text-8xl md:text-9xl lg:text-[10rem] xl:text-[12rem] font-bold tracking-tight mx-4 sm:mx-8 select-none"
                                style={{
                                    fontFamily: "'Rubik', sans-serif",
                                    color: 'transparent',
                                    WebkitTextStroke: '2px #C026D3',
                                    textStroke: '2px #C026D3',
                                }}
                            >
                                THE BAZAAR
                            </span>
                        ))}
                    </div>
                </div>

                {/* Filled Text Overlay */}
                <div className="relative z-10 text-center px-4">
                    <h1
                        className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold mb-4 sm:mb-6 tracking-tight"
                        style={{
                            fontFamily: "'Rubik', sans-serif",
                            color: '#C026D3',
                        }}
                    >
                        THE BAZAAR
                    </h1>
                    <p
                        className="text-base sm:text-lg md:text-xl text-[#6B7280] max-w-lg mx-auto mb-6 sm:mb-8 font-light"
                        style={{ fontFamily: "'Nunito', sans-serif" }}
                    >
                        India's Most Vibrant Fashion Marketplace
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
                        <button
                            onClick={() => navigate('/template20/shop')}
                            className="inline-flex items-center gap-2 px-8 py-3.5 sm:px-10 sm:py-4 bg-[#C026D3] text-white text-sm sm:text-base font-semibold rounded-full hover:bg-[#A21CAF] hover:shadow-lg hover:shadow-[#C026D3]/25 transition-all duration-300 active:scale-95"
                            style={{ fontFamily: "'Nunito', sans-serif" }}
                        >
                            Start Shopping <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                        <button
                            onClick={() => navigate('/template20/shop')}
                            className="inline-flex items-center gap-2 px-8 py-3.5 sm:px-10 sm:py-4 border-2 border-[#C026D3] text-[#C026D3] text-sm sm:text-base font-semibold rounded-full hover:bg-[#C026D3] hover:text-white transition-all duration-300 active:scale-95"
                            style={{ fontFamily: "'Nunito', sans-serif" }}
                        >
                            Explore Collections
                        </button>
                    </div>
                </div>

                {/* Mobile: Horizontal Scroll Product Preview */}
                <div className="md:hidden w-full mt-8 px-4 overflow-x-auto">
                    <div className="flex gap-3 pb-2" style={{ scrollbarWidth: 'none' }}>
                        {products.slice(0, 4).map((p, i) => (
                            <img
                                key={i}
                                src={p.image}
                                alt={p.name}
                                className="w-24 h-32 rounded-xl object-cover shadow-lg flex-shrink-0"
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 2: DEAL STRIP ===== */}
            <section className="w-full overflow-hidden bg-[#C026D3] py-3 sm:py-3.5">
                <div className="deal-scroll flex whitespace-nowrap">
                    {[...deals, ...deals].map((deal, i) => (
                        <span
                            key={i}
                            className="text-white text-xs sm:text-sm font-semibold mx-6 sm:mx-10 flex items-center gap-2"
                            style={{ fontFamily: "'Nunito', sans-serif" }}
                        >
                            <Sparkles className="w-3.5 h-3.5" />
                            {deal}
                        </span>
                    ))}
                </div>
            </section>

            {/* ===== SECTION 3: CATEGORIES ===== */}
            <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 bg-white">
                <div className="max-w-[1400px] mx-auto">
                    <div className="text-center mb-8 sm:mb-12">
                        <p className="text-xs sm:text-sm font-semibold text-[#C026D3] uppercase tracking-[0.2em] mb-2"
                            style={{ fontFamily: "'Nunito', sans-serif" }}>
                            Shop by Category
                        </p>
                        <h2
                            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1A1A1A]"
                            style={{ fontFamily: "'Rubik', sans-serif" }}
                        >
                            Explore the Bazaar
                        </h2>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-5">
                        {categories.map((cat, idx) => (
                            <div
                                key={cat.name}
                                onClick={() => navigate('/template20/shop')}
                                onMouseEnter={() => setHoveredCategory(idx)}
                                onMouseLeave={() => setHoveredCategory(null)}
                                className="group cursor-pointer"
                            >
                                <div
                                    className="relative aspect-[3/4] rounded-2xl overflow-hidden shadow-md transition-all duration-500"
                                    style={{
                                        transform: hoveredCategory === idx ? 'translateY(-6px) scale(1.02)' : 'translateY(0) scale(1)',
                                    }}
                                >
                                    <img
                                        src={cat.image}
                                        alt={cat.name}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                        loading="lazy"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#C026D3]/80 via-[#C026D3]/20 to-transparent" />
                                    <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4">
                                        <h3 className="text-white text-sm sm:text-base font-bold" style={{ fontFamily: "'Rubik', sans-serif" }}>
                                            {cat.name}
                                        </h3>
                                        <p className="text-white/75 text-[10px] sm:text-xs" style={{ fontFamily: "'Nunito', sans-serif" }}>
                                            {cat.count}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 4: PRODUCTS ===== */}
            <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 bg-[#FDF4FF]">
                <div className="max-w-[1400px] mx-auto">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 sm:mb-12 gap-4">
                        <div>
                            <p className="text-xs sm:text-sm font-semibold text-[#C026D3] uppercase tracking-[0.2em] mb-2"
                                style={{ fontFamily: "'Nunito', sans-serif" }}>
                                Curated for You
                            </p>
                            <h2
                                className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1A1A1A]"
                                style={{ fontFamily: "'Rubik', sans-serif" }}
                            >
                                Best Sellers
                            </h2>
                        </div>
                        <button
                            onClick={() => navigate('/template20/shop')}
                            className="inline-flex items-center gap-2 text-sm font-semibold text-[#C026D3] hover:text-[#A21CAF] transition-colors"
                            style={{ fontFamily: "'Nunito', sans-serif" }}
                        >
                            View All <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="bazaar-product-grid">
                        {products.map((product, idx) => (
                            <div
                                key={product.id}
                                onClick={() => navigate(`/template20/product/${product.id}`)}
                                onMouseEnter={() => setHoveredProduct(idx)}
                                onMouseLeave={() => setHoveredProduct(null)}
                                className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500"
                                style={{
                                    transform: hoveredProduct === idx ? 'translateY(-8px) rotate(-0.5deg)' : 'translateY(0) rotate(0)',
                                    transition: 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), box-shadow 0.4s ease',
                                }}
                            >
                                <div className="relative aspect-[3/4] overflow-hidden bg-[#FDF4FF]">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                        loading="lazy"
                                    />
                                    {product.badge && (
                                        <div className="absolute top-2.5 left-2.5 bg-[#C026D3] text-white text-[10px] sm:text-xs font-bold px-2.5 py-0.5 sm:px-3 sm:py-1 rounded-full shadow-lg"
                                            style={{ fontFamily: "'Rubik', sans-serif" }}>
                                            {product.badge}
                                        </div>
                                    )}
                                    <button
                                        onClick={(e) => toggleWishlist(e, product.id)}
                                        className="absolute top-2.5 right-2.5 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-md transition-all duration-300 hover:scale-110"
                                    >
                                        <Heart className={`w-4 h-4 transition-colors duration-300 ${wishlist[product.id] ? 'fill-[#C026D3] text-[#C026D3]' : 'text-gray-400'}`} />
                                    </button>
                                    <div
                                        className="absolute bottom-0 left-0 right-0 p-2.5 sm:p-3 transition-all duration-300"
                                        style={{
                                            transform: hoveredProduct === idx ? 'translateY(0)' : 'translateY(100%)',
                                            opacity: hoveredProduct === idx ? 1 : 0,
                                        }}
                                    >
                                        <button
                                            onClick={(e) => { e.stopPropagation(); }}
                                            className="w-full bg-[#C026D3] text-white py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-semibold flex items-center justify-center gap-2 hover:bg-[#A21CAF] transition-colors"
                                            style={{ fontFamily: "'Nunito', sans-serif" }}
                                        >
                                            <ShoppingBag className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                            Add to Bag
                                        </button>
                                    </div>
                                </div>
                                <div className="p-3 sm:p-4">
                                    <p className="text-[10px] sm:text-xs text-[#C026D3] font-semibold uppercase tracking-wider mb-0.5"
                                        style={{ fontFamily: "'Nunito', sans-serif" }}>
                                        {product.category}
                                    </p>
                                    <h3 className="text-xs sm:text-sm font-semibold text-[#1A1A1A] mb-1 line-clamp-1"
                                        style={{ fontFamily: "'Rubik', sans-serif" }}>
                                        {product.name}
                                    </h3>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm sm:text-base font-bold text-[#1A1A1A]" style={{ fontFamily: "'Rubik', sans-serif" }}>
                                            {'\u20B9'}{product.price.toLocaleString('en-IN')}
                                        </span>
                                        {product.originalPrice && (
                                            <span className="text-[10px] sm:text-xs text-[#6B7280] line-through">
                                                {'\u20B9'}{product.originalPrice.toLocaleString('en-IN')}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 5: TRENDING ===== */}
            <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 bg-white">
                <div className="max-w-[1400px] mx-auto">
                    <div className="text-center mb-8 sm:mb-12">
                        <div className="inline-flex items-center gap-2 bg-[#FDF4FF] text-[#C026D3] px-4 py-1.5 rounded-full text-xs font-semibold mb-3"
                            style={{ fontFamily: "'Nunito', sans-serif" }}>
                            <TrendingUp className="w-3.5 h-3.5" />
                            What's Hot Right Now
                        </div>
                        <h2
                            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1A1A1A]"
                            style={{ fontFamily: "'Rubik', sans-serif" }}
                        >
                            Trending This Week
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                        {trending.map((item, idx) => (
                            <div
                                key={idx}
                                onClick={() => navigate('/template20/shop')}
                                className="group relative cursor-pointer rounded-2xl overflow-hidden shadow-lg aspect-[4/3] sm:aspect-[3/4]"
                            >
                                <img
                                    src={item.image}
                                    alt={item.name}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                    loading="lazy"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
                                    <div className="inline-flex items-center gap-1.5 bg-[#C026D3] text-white px-3 py-1 rounded-full text-[10px] sm:text-xs font-bold mb-2"
                                        style={{ fontFamily: "'Rubik', sans-serif" }}>
                                        <Zap className="w-3 h-3" />
                                        {item.tag}
                                    </div>
                                    <h3 className="text-lg sm:text-xl font-bold text-white" style={{ fontFamily: "'Rubik', sans-serif" }}>
                                        {item.name}
                                    </h3>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 6: TESTIMONIALS ===== */}
            <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 bg-[#FDF4FF]">
                <div className="max-w-[800px] mx-auto text-center">
                    <p className="text-xs sm:text-sm font-semibold text-[#C026D3] uppercase tracking-[0.2em] mb-2"
                        style={{ fontFamily: "'Nunito', sans-serif" }}>
                        Customer Love
                    </p>
                    <h2
                        className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1A1A1A] mb-8 sm:mb-12"
                        style={{ fontFamily: "'Rubik', sans-serif" }}
                    >
                        What Our Shoppers Say
                    </h2>

                    <div
                        className="relative"
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                    >
                        <Quote className="w-10 h-10 sm:w-12 sm:h-12 text-[#C026D3]/20 mx-auto mb-4" />

                        <div className="min-h-[180px] sm:min-h-[160px] flex items-center justify-center">
                            <div className="px-2">
                                <div className="flex items-center justify-center gap-1 mb-4">
                                    {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                                        <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 fill-[#C026D3] text-[#C026D3]" />
                                    ))}
                                </div>
                                <p
                                    className="text-base sm:text-lg text-[#1A1A1A]/80 leading-relaxed mb-5 italic"
                                    style={{ fontFamily: "'Nunito', sans-serif" }}
                                >
                                    "{testimonials[activeTestimonial].text}"
                                </p>
                                <p className="font-bold text-[#1A1A1A] text-sm sm:text-base" style={{ fontFamily: "'Rubik', sans-serif" }}>
                                    {testimonials[activeTestimonial].name}
                                </p>
                                <p className="text-[#6B7280] text-xs sm:text-sm" style={{ fontFamily: "'Nunito', sans-serif" }}>
                                    {testimonials[activeTestimonial].city}
                                </p>
                            </div>
                        </div>

                        {/* Dots */}
                        <div className="flex items-center justify-center gap-2 mt-6">
                            {testimonials.map((_, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => setActiveTestimonial(idx)}
                                    className={`rounded-full transition-all duration-300 ${activeTestimonial === idx ? 'w-8 h-2 bg-[#C026D3]' : 'w-2 h-2 bg-[#C026D3]/30'}`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 7: VIBRANT NEWSLETTER ===== */}
            <section
                className="py-14 sm:py-20 px-4 sm:px-6 text-center"
                style={{
                    background: 'linear-gradient(135deg, #C026D3 0%, #A21CAF 40%, #86198F 100%)',
                }}
            >
                <div className="max-w-[600px] mx-auto">
                    <Sparkles className="w-8 h-8 sm:w-10 sm:h-10 text-white/80 mx-auto mb-4" />
                    <h2
                        className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3"
                        style={{ fontFamily: "'Rubik', sans-serif" }}
                    >
                        Don't Miss a Deal!
                    </h2>
                    <p className="text-white/70 text-sm sm:text-base mb-6 sm:mb-8" style={{ fontFamily: "'Nunito', sans-serif" }}>
                        Subscribe to get exclusive offers, new arrivals, and 10% off your first order.
                    </p>
                    <form onSubmit={(e) => { e.preventDefault(); setEmail(''); }} className="flex max-w-md mx-auto">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="flex-1 px-4 sm:px-5 py-3 sm:py-3.5 bg-white/15 border border-white/25 rounded-l-full text-white placeholder-white/50 text-sm outline-none focus:bg-white/20 transition-colors"
                            style={{ fontFamily: "'Nunito', sans-serif" }}
                        />
                        <button
                            type="submit"
                            className="px-5 sm:px-7 py-3 sm:py-3.5 bg-white text-[#C026D3] rounded-r-full font-bold text-sm hover:bg-[#FDF4FF] transition-colors"
                            style={{ fontFamily: "'Nunito', sans-serif" }}
                        >
                            Subscribe
                        </button>
                    </form>
                </div>
            </section>
        </div>
    );
};

export default Template20;
